import { breakpoints, type Breakpoint } from "@/lib/design-tokens/breakpoints";
import type { LayoutProps } from "@/lib/utils";
import { createContext, useContext, useState, type ReactNode } from "react";

export const SIDEBAR_BREAKPOINT: Breakpoint = "md";

const SidebarProviderContext = createContext(
  {} as {
    open: boolean;
    setOpen: (open: boolean) => void;
    submenu: ReactNode | null;
    setSubmenu: (submenu: ReactNode | null) => void;
    submenuOpen: boolean;
    setSubmenuOpen: (submenuOpen: boolean) => void;
  },
);

export default function SidebarProvider({ children }: LayoutProps) {
  // Check if the viewport is above the sidebar breakpoint
  const mediaQuery = window.matchMedia(
    `(min-width: ${breakpoints[SIDEBAR_BREAKPOINT]})`,
  );

  // Open by default if above sidebar breakpoint, closed otherwise
  const [open, setOpen] = useState<boolean>(mediaQuery.matches);

  // Submenu state is managed by the consumer, allows them to inject their own submenu content into the sidebar.
  const [submenu, setSubmenu] = useState<ReactNode | null>(null);

  // Submenu open state is separate from main sidebar open state
  // Controls whether the submenu is expanded/collapsed (above breakpoint) or visible (below breakpoint)
  const [submenuOpen, setSubmenuOpen] = useState<boolean>(mediaQuery.matches);

  return (
    <SidebarProviderContext.Provider
      value={{
        open,
        setOpen,
        submenu,
        setSubmenu,
        submenuOpen,
        setSubmenuOpen,
      }}
    >
      {children}
    </SidebarProviderContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarProviderContext);

  if (context === undefined)
    throw new Error("useSidebar must be used within a SidebarProvider");

  return context;
}
